import type { ChangePasswordResponse, ValidateResetTokenResponse } from 'src/api/auth';

import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { paths } from 'src/routes/paths';

import { useUserStore } from 'src/store/user';
import { refreshCSRFToken } from 'src/api/utils/csrf-token-manager';
import { signOut, changePassword, validateResetToken, getLoggedInUserInfo } from 'src/api/auth';

export function useAuth() {
  const setUser = useUserStore((s) => s.setUser);
  const { data: user, isLoading } = useQuery({
    queryKey: ['user', 'my-info'],
    queryFn: getLoggedInUserInfo,
  });

  // Use useEffect to prevent infinite loops
  useEffect(() => {
    if (user) {
      setUser(user);
    }
  }, [user, setUser]);

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
  };
}

export function useLogout() {
  const queryClient = useQueryClient();
  const setUser = useUserStore((s) => s.setUser);
  const navigate = useNavigate();

  const { mutate: logoutMutate } = useMutation({
    mutationFn: signOut,
    onSuccess: async () => {
      try {
        await refreshCSRFToken();
      } catch (error) {
        console.warn('Failed to refresh CSRF token after logout:', error);
      } finally {
        queryClient.removeQueries();
        setUser(null);
        navigate(paths.root);
      }
    },
    onError: () => {
      // [TODO] handle error
    },
  });

  const logout = () => {
    logoutMutate();
  };

  return {
    logout,
  };
}

export function useValidateResetToken(token: string) {
  return useQuery<ValidateResetTokenResponse>({
    queryKey: ['validateResetToken', token],
    queryFn: () => validateResetToken(token),
    enabled: !!token,
    retry: false,
  });
}

export function useChangePassword() {
  return useMutation<ChangePasswordResponse, Error, { token: string; password: string }>({
    mutationFn: ({ token, password }) => changePassword({ token, password }),
  });
}
